/**
 * This will return the sign in url with the appropriate query params for source and dest, and also handle localized routes with url prefixes
 * @param source (optional) - this is what tells the signin page what the user clicked on
 * @param dest (optional) - this is where the user should be redirected after signin, if not provided, it will redirect to the current page
 * @param urlPrefix (optional) - this is the url prefix for localized routes, if not provided, it will default to no prefix
 * @returns string - the full url to the signin page with query params and hash.
 */
const signinUrlPath = (source?: string, dest?: string, urlPrefix?: string) => {
  const currentPath =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "";
  const params = new URLSearchParams();
  if (source) {
    params.set("source", source);
  }
  const redirectDest = dest ? dest : currentPath;
  params.delete("dest");
  params.set("dest", redirectDest);
  const signinPath = urlPrefix?.length ? `/${urlPrefix}/signin` : "/signin";
  const url = `${signinPath}?${params.toString()}`;
  return url;
};

/**
 * This will return the sign up url with the appropriate query params for source and dest, and also handle localized routes with url prefixes
 * @param source (optional) - this is what tells the signup page what the user clicked on
 * @param dest (optional) - this is where the user should be redirected after signup, if not provided, it will redirect to the current page
 * @param urlPrefix (optional) - this is the url prefix for localized routes, if not provided, it will default to no prefix
 * @returns string - the url to the signup page with the appropriate query params and hash.
 */
const signupUrlPath = (source?: string, dest?: string, urlPrefix?: string) => {
  const currentPath =
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "";
  const params = new URLSearchParams();
  if (source) {
    params.set("source", source);
  }
  const redirectDest = dest ? dest : currentPath;
  params.delete("dest");
  params.set("dest", redirectDest);
  const signupPath = urlPrefix?.length ? `/${urlPrefix}/signup` : "/signup";
  const url = `${signupPath}?${params.toString()}`;
  return url;
};

/**
 * Creates a base64 encoded string from a UTF-8 string.
 * It will allow for proper encoding of special characters and emojis.
 * UTF-8 → bytes → base64
 */
const toBase64Utf8 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
};

// TODO: move this to a more appropriate location, maybe routes.utils file, since it is not auth specific
/**
 * Will set a query param on a URLSearchParams object, optionally encoding the value in base64.
 * Skip empty values so we never emit an undefined query param ie `param=` or `param=undefined`
 */
const setNewParam = ({
  encode,
  key,
  params,
  value,
}: {
  encode?: boolean;
  key: string;
  params: URLSearchParams;
  value?: string | null;
}) => {
  if (value) {
    params.set(key, encode ? toBase64Utf8(value) : value);
  }
};

export { signinUrlPath, signupUrlPath, toBase64Utf8, setNewParam };
